import Chip from '@mui/joy/Chip'
import Sheet from '@mui/joy/Sheet'
import Table from '@mui/joy/Table'
import Typography from '@mui/joy/Typography'
import { DocumentPipelineDay } from '@numeo/types-core'
import { formatAuditDay, formatDuration, percent } from '../format'
import OutcomeBar from './OutcomeBar'
import InfoHint from './InfoHint'
import { HINTS } from '../hints'
import { timeZoneName, VIEWER_TIME_ZONE } from '../viewerTimeZone'

/**
 * One row per day: volume, outcomes, route, latency and how the shadow run compared with the legacy chain. A row is the
 * day selector: clicking it (or pressing Enter or Space on it) shows that day's numbers everywhere else on the page.
 */
export default function DailyTable({ days, selectedDay, onSelect }: { days: DocumentPipelineDay[]; selectedDay: string; onSelect: (day: string) => void }) {
    const max = Math.max(0, ...days.map((d) => d.enqueued))
    return (
        <Sheet variant="outlined" sx={{ borderRadius: 'md', overflow: 'auto' }}>
            <Table
                size="sm"
                stickyHeader
                hoverRow
                sx={{
                    minWidth: 1100,
                    '& th, & td': { whiteSpace: 'nowrap' },
                    // The selected day: a tinted row with an accent bar on its left edge.
                    '& tr.audit-day-selected td': { bgcolor: 'primary.softBg' },
                    '& tr.audit-day-selected td:first-of-type': { boxShadow: 'inset 3px 0 0 var(--joy-palette-primary-solidBg)' },
                }}
            >
                <thead>
                    <tr>
                        <th style={{ width: 140 }}>
                            <InfoHint hint={HINTS.day}>Day ({timeZoneName(VIEWER_TIME_ZONE, true)})</InfoHint>
                        </th>
                        <th style={{ width: 180 }}>
                            <InfoHint hint={HINTS.outcomes}>Outcomes</InfoHint>
                        </th>
                        <th>
                            <InfoHint hint={HINTS.enqueued}>Enqueued</InfoHint>
                        </th>
                        <th>
                            <InfoHint hint={HINTS.done}>Done</InfoHint>
                        </th>
                        <th>
                            <InfoHint hint={HINTS.notOfInterest}>Not of interest</InfoHint>
                        </th>
                        <th>
                            <InfoHint hint={HINTS.combined}>Several documents</InfoHint>
                        </th>
                        <th>
                            <InfoHint hint={HINTS.failed}>Failed</InfoHint>
                        </th>
                        <th>
                            <InfoHint hint={HINTS.inFlight}>In flight</InfoHint>
                        </th>
                        <th>
                            <InfoHint hint={HINTS.deduplicated}>Deduplicated</InfoHint>
                        </th>
                        <th>
                            <InfoHint hint={HINTS.textVisual}>Text / visual</InfoHint>
                        </th>
                        <th>
                            <InfoHint hint={HINTS.latency}>p50 / p95</InfoHint>
                        </th>
                        <th>
                            <InfoHint hint={HINTS.shadowAgreedColumn}>Shadow agreed</InfoHint>
                        </th>
                        <th>
                            <InfoHint hint={HINTS.avgScore}>Avg score</InfoHint>
                        </th>
                        <th>
                            <InfoHint hint={HINTS.legacyPendingNone}>Pipeline only / legacy only</InfoHint>
                        </th>
                        <th>
                            <InfoHint hint={HINTS.handlerGaveUpColumn}>Handler gave up</InfoHint>
                        </th>
                    </tr>
                </thead>
                <tbody>
                    {days.map((d) => (
                        <tr
                            key={d.day}
                            role="button"
                            tabIndex={0}
                            aria-pressed={d.day === selectedDay}
                            onClick={() => onSelect(d.day)}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault()
                                    onSelect(d.day)
                                }
                            }}
                            style={{ cursor: 'pointer', outlineOffset: -2 }}
                            className={d.day === selectedDay ? 'audit-day-selected' : undefined}
                        >
                            <td>
                                <Typography level="body-sm" fontWeight={d.day === selectedDay ? 'xl' : 'md'}>
                                    {formatAuditDay(d.day)}
                                </Typography>
                            </td>
                            <td>
                                <OutcomeBar day={d} max={max} />
                            </td>
                            <td>{d.enqueued}</td>
                            <td>{d.done}</td>
                            <td>{d.notOfInterest}</td>
                            <td>{d.combined}</td>
                            <td>
                                {d.failed > 0 ? (
                                    <Chip size="sm" color="danger" variant="soft">
                                        {d.failed} ({percent(d.failed, d.enqueued)})
                                    </Chip>
                                ) : (
                                    0
                                )}
                            </td>
                            <td>{d.inFlight}</td>
                            <td>{d.deduplicated}</td>
                            <td>
                                {d.textRoute} / {d.visualRoute}
                            </td>
                            <td>
                                {formatDuration(d.p50Seconds)} / {formatDuration(d.p95Seconds)}
                            </td>
                            <td>{d.shadow.compared ? `${d.shadow.agreed} of ${d.shadow.compared} (${percent(d.shadow.agreed, d.shadow.compared)})` : '—'}</td>
                            <td>{d.shadow.averageScore ?? '—'}</td>
                            <td>
                                {d.shadow.legacyNone} / {d.shadow.legacyOnly ?? 0}
                            </td>
                            <td>
                                {d.resultHandlerFailed > 0 ? (
                                    <Chip size="sm" color="warning" variant="soft">
                                        {d.resultHandlerFailed}
                                    </Chip>
                                ) : (
                                    0
                                )}
                            </td>
                        </tr>
                    ))}
                    {days.length === 0 && (
                        <tr>
                            <td colSpan={14}>
                                <Typography level="body-sm" sx={{ py: 2, textAlign: 'center' }}>
                                    No documents in this window.
                                </Typography>
                            </td>
                        </tr>
                    )}
                </tbody>
            </Table>
        </Sheet>
    )
}
